import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, CircleNotch, ArrowUpRight, XCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function SponsorSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState({ phase: "polling", attempts: 0, data: null, error: "" });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", error: "Missing session_id in URL", attempts: 0, data: null });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    async function poll() {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/sponsorship/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "success", attempts, data, error: "" });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", attempts, data, error: "" });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", attempts, data, error: "" });
          return;
        }
        setState({ phase: "polling", attempts, data, error: "" });
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ phase: "error", attempts, data: null, error: e?.response?.data?.detail || e.message });
      }
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="sponsor-success-page" className="px-6 md:px-10 lg:px-16 py-16 md:py-24 max-w-2xl mx-auto">
      {state.phase === "polling" && <Polling attempts={state.attempts} />}
      {state.phase === "success" && <Success data={state.data} />}
      {state.phase === "expired" && <Expired />}
      {state.phase === "timeout" && <Timeout />}
      {state.phase === "error" && <ErrorPanel msg={state.error} />}
    </div>
  );
}

function Polling({ attempts }) {
  return (
    <div className="text-center" data-testid="sponsor-status-polling">
      <CircleNotch size={36} weight="bold" className="mx-auto text-blue-700 animate-spin mb-6" />
      <div className="overline mb-3">Confirming payment</div>
      <h1 className="font-display font-bold text-4xl tracking-tight mb-3">Hang tight…</h1>
      <p className="text-zinc-600">We're checking with Stripe ({attempts}/{MAX_ATTEMPTS}). This usually takes a few seconds.</p>
    </div>
  );
}

function Success({ data }) {
  const until = data?.sponsored_until ? new Date(data.sponsored_until) : null;
  return (
    <div className="text-center" data-testid="sponsor-status-success">
      <CheckCircle size={48} weight="fill" className="mx-auto text-blue-700 mb-6" />
      <div className="overline mb-3">Payment confirmed</div>
      <h1 className="font-display font-black text-5xl tracking-[-0.04em] leading-none mb-4">You're live.</h1>
      <p className="text-zinc-600 leading-relaxed">
        <strong>{data?.conference_title || "Your conference"}</strong> is now sponsored across Symposia.
        {until && (<><br />Slot active through <span className="font-mono text-zinc-950">{until.toLocaleDateString()}</span>.</>)}
      </p>
      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        {data?.conference_id && (
          <Link to={`/conferences/${data.conference_id}`} data-testid="sponsor-success-view-conf" className="btn-primary">View listing <ArrowUpRight size={14} data-arrow="forward" /></Link>
        )}
        <Link to="/" className="btn-secondary">Back to home</Link>
      </div>
    </div>
  );
}

function Expired() {
  return (
    <div className="text-center">
      <XCircle size={48} className="mx-auto text-zinc-400 mb-6" />
      <h1 className="font-display font-bold text-4xl tracking-tight mb-3">Session expired</h1>
      <p className="text-zinc-600">The checkout session expired before payment completed. You can try again.</p>
      <div className="mt-6"><Link to="/sponsor" className="btn-primary">Try again</Link></div>
    </div>
  );
}

function Timeout() {
  return (
    <div className="text-center">
      <CircleNotch size={36} className="mx-auto text-zinc-400 mb-6" />
      <h1 className="font-display font-bold text-3xl tracking-tight mb-3">Still confirming</h1>
      <p className="text-zinc-600 max-w-md mx-auto">Stripe is taking a bit longer than usual. We'll activate your slot as soon as the payment clears — typically within a few minutes. You can safely close this page.</p>
      <div className="mt-6"><Link to="/" className="btn-secondary">Back to home</Link></div>
    </div>
  );
}

function ErrorPanel({ msg }) {
  return (
    <div className="text-center">
      <XCircle size={48} className="mx-auto text-red-500 mb-6" />
      <h1 className="font-display font-bold text-3xl tracking-tight mb-3">Something went wrong</h1>
      <p className="text-zinc-600">{msg || "Unexpected error."} Please <a href="mailto:hello@conferencely.org" className="underline">email us</a> and we'll sort it out.</p>
    </div>
  );
}
